import cx from 'classnames';
import dynamic from 'next/dynamic';
import { useContext } from 'react';
import FeatureBranch from '@/common/FeatureBranch';
import PageFooter from '@/common/PageFooter';
import PageHeader from '@/common/PageHeader';
import BelowFoldViewHandler from '@/vdp/Analytics/BelowFoldViewHandler';
import BreadCrumbs from '@/vdp/BreadCrumbs';
import DeliveryUnavailableBanner from '@/vdp/DeliveryUnavailableBanner';
import dynamicLoader from '@/vdp/dynamicLoader';
import Gallery from '@/vdp/Gallery';
import Hero from '@/vdp/Hero';
import FeaturesAndHighlights from '~/components/vdp/FeaturesAndHighlights';
import Sections from '~/constants/vdp/sections';
import ModalContext from '~/context/vdp/ModalContext';
import { useShowPriceDetailsImmediately } from '~/hooks/vdp';
import useShouldLoadBelowFirstSectionStandard from '~/hooks/vdp/selectors/useShouldLoadBelowFirstSectionStandard';
import useShouldLoadFirstSection from '~/hooks/vdp/selectors/useShouldLoadFirstSection';
import NativeError from '~/native/NativeError';
import { ErrorBoundary } from '~/utilities/ErrorBoundary';
import { useShouldImport } from './hooks';
import SectionsWrapper from './SectionsWrapper';
import ShowConditionally from './ShowConditionally';
import { Wrapper } from './VehicleDetailsContainer.styles';

const AboutThePrice = dynamic(
  () => import(/* webpackChunkName: "AboutThePrice" */ '@/vdp/AboutThePrice')
);

const AncillaryProducts = dynamic(
  () =>
    import(
      /* webpackChunkName: "AncillaryProducts" */ '@/vdp/AncillaryProducts'
    ),
  { ssr: false }
);

const CreditKarmaPromoBanner = dynamic(
  () =>
    import(
      /* webpackChunkName: "CreditKarmaPromoBanner" */ '@/common/CreditKarmaPromoBanner'
    ),
  { ssr: false }
);

const Disclaimer = dynamic(
  () => import(/* webpackChunkName: "Disclaimer" */ '@/vdp/Disclaimer'),
  {
    ssr: false,
    loading: dynamicLoader(Sections.Disclaimer)
  }
);

const DesktopPageNavigation = dynamic(
  () =>
    import(
      /* webpackChunkName: "DesktopPageNavigation" */ '@/vdp/DesktopPageNavigation'
    )
);

const Modals = dynamic(
  () => import(/* webpackChunkName: "Modals" */ './Modals'),
  { ssr: false }
);

const MoneyBackGuarantee = dynamic(
  () =>
    import(
      /* webpackChunkName: "MoneyBackGuarantee" */ '@/vdp/MoneyBackGuarantee'
    ),
  { ssr: false }
);

const One50PointInspection = dynamic(
  () => import(/* webpackChunkName: "150PointInspection" */ '@/vdp/Inspection'),
  { ssr: false, loading: dynamicLoader(Sections.Inspection) }
);

const OwnerReviews = dynamic(
  () => import(/* webpackChunkName: "OwnerReviews" */ '@/vdp/OwnerReviews'),
  {
    ssr: false,
    loading: dynamicLoader(Sections.OwnerReviews)
  }
);

const PickupAndDelivery = dynamic(
  () =>
    import(
      /* webpackChunkName: "PickupAndDelivery" */ '@/vdp/PickupAndDelivery'
    )
);

const PriceDetails = dynamic(
  () => import(/* webpackChunkName: "PriceDetails" */ '@/vdp/PriceDetails'),
  {
    ssr: false,
    loading: dynamicLoader(Sections.PriceDetails)
  }
);

const RecommendedVehicles = dynamic(
  () =>
    import(
      /* webpackChunkName: "RecommendedVehicles" */ '@/vdp/RecommendedVehicles'
    ),
  {
    ssr: false
  }
);

const ReservableHeader = dynamic(
  () =>
    import(
      /* webpackChunkName: "ReservableHeader" */ '@/vdp/Reservable/ReservableHeader'
    )
);

const StatusBanner = dynamic(
  () => import(/* webpackChunkName: "StatusBanner" */ '@/vdp/StatusBanner'),
  { ssr: false }
);

const StickyBanner = dynamic(
  () => import(/* webpackChunkName: "StickyBanner" */ '@/vdp/StickyBanner'),
  { ssr: false }
);

const StickyCta = dynamic(
  () => import(/* webpackChunkName: "StickyCta" */ '@/vdp/StickyCta'),
  { ssr: false }
);

const VehicleDetails = dynamic(
  () => import(/* webpackChunkName: "VehicleDetails" */ '@/vdp/VehicleDetails'),
  {
    ssr: false,
    loading: dynamicLoader(Sections.VehicleDetails)
  }
);

const VehicleDetailsStandardContainer = () => {
  const shouldLoadFirstSection = useShouldLoadFirstSection();
  const shouldLoadBelowFirstSection = useShouldLoadBelowFirstSectionStandard();
  const { importReservableHeader } = useShouldImport();
  const showPriceDetailsImmediately = useShowPriceDetailsImmediately();
  const { setCreditKarmaPromoModalOpen } = useContext(ModalContext);

  return (
    <>
      <PageHeader />
      <FeatureBranch />
      <NativeError />
      <Wrapper id={Sections.Root}>
        <CreditKarmaPromoBanner
          setCreditKarmaPromoModalOpen={setCreditKarmaPromoModalOpen}
        />
        <ShowConditionally wip={false}>
          <BreadCrumbs />
          <StatusBanner />
        </ShowConditionally>
        <DeliveryUnavailableBanner />
        <SectionsWrapper>
          <ShowConditionally native={false}>
            <StickyBanner />
          </ShowConditionally>
          {importReservableHeader && <ReservableHeader />}
          <ShowConditionally wip>
            <BreadCrumbs />
            <StatusBanner />
          </ShowConditionally>
          <Hero />
          <ShowConditionally desktop>
            <DesktopPageNavigation />
          </ShowConditionally>

          <ErrorBoundary sectionId={Sections.Gallery}>
            <div className="relative bg-grey-1">
              <ShowConditionally wip={false}>
                <Gallery />
              </ShowConditionally>

              <ShowConditionally notAvailableForImmediatePurchase>
                <div id={Sections.RecommendedVehicles}>
                  <ShowConditionally renderBelowSpinner>
                    <RecommendedVehicles />
                  </ShowConditionally>
                </div>
              </ShowConditionally>

              <ShowConditionally aboutPrice>
                <AboutThePrice />
              </ShowConditionally>

              <ShowConditionally deliveryPickup>
                <PickupAndDelivery />
              </ShowConditionally>
            </div>
          </ErrorBoundary>

          <FeaturesAndHighlights />

          <BelowFoldViewHandler
            id={Sections.VehicleDetails}
            className={cx(Sections.VehicleDetails, {
              'will-load': !shouldLoadFirstSection
            })}
          >
            <ShowConditionally renderFirstSection>
              <VehicleDetails />
            </ShowConditionally>
          </BelowFoldViewHandler>

          <BelowFoldViewHandler
            id={Sections.PriceDetails}
            className={cx(Sections.PriceDetails, {
              'will-load': !shouldLoadBelowFirstSection
            })}
          >
            {(shouldLoadBelowFirstSection || showPriceDetailsImmediately) && (
              <PriceDetails />
            )}
          </BelowFoldViewHandler>

          <BelowFoldViewHandler
            id={Sections.OwnerReviews}
            className={cx(Sections.OwnerReviews, {
              'will-load': !shouldLoadBelowFirstSection
            })}
          >
            <ShowConditionally ownerReviews renderBelowFirstSection>
              <OwnerReviews />
            </ShowConditionally>
          </BelowFoldViewHandler>

          <BelowFoldViewHandler
            id={Sections.Inspection}
            className={cx(Sections.Inspection, {
              'will-load': !shouldLoadBelowFirstSection
            })}
          >
            <ShowConditionally renderBelowFirstSection>
              <One50PointInspection />
            </ShowConditionally>
          </BelowFoldViewHandler>

          <BelowFoldViewHandler
            id={Sections.MoneyBackGuarantee}
            className={cx(Sections.MoneyBackGuarantee, {
              'will-load': !shouldLoadBelowFirstSection
            })}
          >
            <ShowConditionally renderBelowFirstSection>
              <MoneyBackGuarantee />
            </ShowConditionally>
          </BelowFoldViewHandler>

          <BelowFoldViewHandler
            id={Sections.AncillaryProducts}
            className={cx(Sections.AncillaryProducts, {
              'will-load': !shouldLoadBelowFirstSection
            })}
          >
            <ShowConditionally renderBelowFirstSection>
              <AncillaryProducts />
            </ShowConditionally>
          </BelowFoldViewHandler>

          <ShowConditionally notAvailableForImmediatePurchase={false}>
            <div id={Sections.RecommendedVehicles}>
              <ShowConditionally renderBelowFirstSection>
                <RecommendedVehicles />
              </ShowConditionally>
            </div>
          </ShowConditionally>
        </SectionsWrapper>

        <ShowConditionally renderBelowFirstSection>
          <Disclaimer />
        </ShowConditionally>

        <ShowConditionally renderStickyCta renderBelowSpinner>
          <StickyCta />
        </ShowConditionally>
      </Wrapper>
      <PageFooter />
      <Modals />
    </>
  );
};

export default VehicleDetailsStandardContainer;
